import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { QuoteState } from '../types/stateProviders/QuoteStateProvider';

const QuoteStateContext = createContext<any | null>(null);
const QuoteDispatchContext = createContext<
	(newState: Partial<QuoteState>) => void
>(() => {});

export const QuoteStateProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const initialQuoteState: QuoteState = {
		currentQuote: null,
		deckingTypes: [],
		railingTypes: [],
		railCapStyles: [],
		selectedOptionIndex: null,
		materialCost: {},
		options: [
			{
				deckingType: null,
				railingType: null,
				railCapStyle: null,
				accessories: [],
			},
			{
				deckingType: null,
				railingType: null,
				railCapStyle: null,
				accessories: [],
			},
			{
				deckingType: null,
				railingType: null,
				railCapStyle: null,
				accessories: [],
			},
		], // Array to store configurations for each option
		demoSquareFootage: '',
		demoHeight: '',
		selectedPermit: '',
		selectedPermitCost: 0,
		septicFeeRequired: false,
		engineeringFeeRequired: false,
		miscMaterialCosts: '',
		miscLaborCosts: '',
		appliedDiscounts: [],
		projectName: '',
		corners: [],
		blocks: [],
		pixelToInchRatio: 0,
		panOffset: {
			x: 0,
			y: 0,
		},
		zoomLevel: 1,
		deckboxHeightInFeet: 0,
		drawingErrors: [],
		optionsErrors: [],
		requiresFascia: false,
		permitRequired: null,
		designPackage: null,
	};

	const [quoteState, setQuoteState] = useState<QuoteState>(initialQuoteState);

	const updateQuoteState = useCallback((newQuoteState: Partial<QuoteState>) => {
		setQuoteState((prevState: QuoteState) => {
			if (hasOptions(newQuoteState)) {
				const updatedOptions = prevState.options.map((option, index) =>
					newQuoteState.options[index]
						? { ...option, ...newQuoteState.options[index] }
						: option
				);
				return { ...prevState, ...newQuoteState, options: updatedOptions };
			}
			return { ...prevState, ...newQuoteState };
		});
	}, []);

	useEffect(() => {
		let pricingPageValid = false;
		const errors = [];
		//Validate at least one option is configured correctly
		quoteState.options.forEach((option) => {
			if (
				option.deckingType &&
				option.railingType &&
				(option.railingType !== 'IRX' || option.railCapStyle)
			) {
				pricingPageValid = true;
			}
		});
		if (!pricingPageValid) {
			errors.push('At least one option in Step 3 must be fully configured');
		}

		updateQuoteState({ optionsErrors: errors });
	}, [quoteState.options]);

	return (
		<QuoteStateContext.Provider value={quoteState}>
			<QuoteDispatchContext.Provider value={updateQuoteState}>
				{children}
			</QuoteDispatchContext.Provider>
		</QuoteStateContext.Provider>
	);
};

export function useQuoteState(): QuoteState {
	return useContext(QuoteStateContext);
}

export function useQuoteDispatch(): React.Dispatch<Partial<QuoteState>> {
	return useContext(QuoteDispatchContext);
}

function hasOptions(
	state: Partial<QuoteState>
): state is Partial<QuoteState> & { options: QuoteState['options'] } {
	return Array.isArray(state.options);
}
